import { NextFrame } from "@pdfking/reactivity/vue"
import { Max, Min } from "@pdfking/math"
import { contains, first, last, length, map, object_map } from "@pdfking/rfunc"
import {
  IsString,
  Items,
  KeyDrop,
  Keys,
  RecordMerge,
  Values,
  Void,
} from "@pdfking/tfunc"
import fire_event from "@pdfking/workflow/utils/fire_event"
import { defineComponent, withModifiers } from "vue"
import keyboard from "../constants/keyboard"
import can_hover from "../features/can_hover"
import search from "../mixins/Search"
import strip_tags from "../utils/strip_tags"
import truncate from "../utils/truncate"

export interface SelectionInfo {
  id: string
  unicode: string
  group?: string
  score?: number
}

export default defineComponent({
  computed: {
    active_key() {
      return Keys(this.results)[this.active]
    },
    all_options() {
      return this.options || {}
    },
    default_results() {
      if (this.show_all) {
        return this.all_options
      }

      if (this.multiple) {
        return this.selection
      }

      return null
    },

    input_placeholder() {
      const res = strip_tags(
        map((s: SelectionInfo) => s.unicode, Values(this.selection)).join(", ")
      )

      if (length(this.selection) > 1) {
        return `${length(this.selection)} elementi: ${res}`
      }

      return res
    },
  },
  data() {
    return {
      active: -1,
      open: false,
      query: "",
      selection: this.options
        ? object_map(
            (v: string | SelectionInfo) => (IsString(v) ? v : v.id),
            (v: string | SelectionInfo) => (IsString(v) ? this.options[v] : v),
            this.value
          )
        : {},
      show_all: true,
    }
  },
  methods: {
    contains,
    first,
    keydown(e: KeyboardEvent) {
      this.show_all = true

      switch (e.keyCode) {
        case keyboard.BACKSPACE:
          if (!this.query) {
            this.pop()
          }
          break
        case keyboard.ESCAPE:
          this.open = false
          this.query = ""
          break
        case keyboard.ARROW_DOWN:
          this.active = Min(
            length(this.results) - 1,
            this.active + (this.open ? 1 : 0)
          )
          this.open = true

          e.preventDefault()
          break
        case keyboard.ARROW_UP:
          this.active = Min(
            Max(-1, this.active - (this.open ? 1 : 0)),
            length(this.results) - 1
          )
          this.open = this.active >= 0

          this.query = this.open ? this.query : ""

          e.preventDefault()

          break
        case keyboard.ENTER:
          if (this.open && this.active >= 0) {
            // @ts-expect-error TS2345 TODO: fix vue tsx types
            this.toggle(Values(this.results)[this.active])
          }
          e.preventDefault()
          break
        default:
          this.open = true

          this.query = (this.$refs.input as HTMLInputElement).value

          break
      }
    },
    length,
    pop() {
      if (length(this.selection) > 0) {
        this.selection = KeyDrop(this.selection, [last(Keys(this.selection))])
      }
    },
    toggle(info: SelectionInfo) {
      if (this.multiple) {
        if (contains(this.selection, info.id)) {
          this.selection = KeyDrop(this.selection, [info.id])
        } else {
          this.selection = RecordMerge(this.selection, { [info.id]: info })
        }
      } else {
        this.open = false
        this.query = ""
        this.selection = { [info.id]: info }
      }

      if (can_hover()) (this.$refs.input as HTMLInputElement).focus()
    },
    truncate,
    Values,
  },
  mixins: [search],
  props: [
    "name",
    "value",
    "options",
    "disabled",
    "multiple",
    "hide_dropdown",
  ] as const,
  render() {
    return (
      <div
        class="input-autocomplete"
        onClick={withModifiers(Void, ["prevent"])}
      >
        <div class={{ "input-group": !this.hide_dropdown }}>
          <input
            ref="input"
            value={this.query}
            onInput={(e: Event) => {
              this.query = (e.target as HTMLInputElement).value
            }}
            class="form-control"
            placeholder={this.input_placeholder}
            onBlur={() => {
              this.open = false
              this.query = ""
            }}
            onClick={() => {
              this.open = true
              this.show_all = false
            }}
            onKeydown={this.keydown}
          />

          {Items(this.selection).map(([id, _info]: [string, any]) => (
            <input
              key={id}
              ref="hidden"
              name={this.name}
              type="hidden"
              value={id}
            />
          ))}

          {!this.hide_dropdown && (
            <select
              ref="hidden"
              class="form-select flex-grow-0"
              onClick={withModifiers(() => {
                this.open = !this.open || !this.show_all
                this.query = ""
                this.show_all = true
              }, ["stop", "prevent"])}
              onMousedown={withModifiers(Void, ["stop", "prevent"])}
              onMouseup={withModifiers(Void, ["stop", "prevent"])}
            ></select>
          )}
        </div>
        {this.open && length(this.results) > 0 && (
          <div class="autocomplete-menu no-scrollbar">
            <ul class="dropdown-list dropdown-menu d-flex flex-column">
              {Values(this.results).map((info: any, i: number) => (
                <li
                  key={i}
                  class={[
                    "dropdown-item onhover",
                    {
                      selected: contains(this.selection, info.id),
                      active: info.id == this.active_key,
                    },
                  ]}
                  style={{ order: info.score || 0 }}
                  onClick={withModifiers(
                    () => this.toggle(info),
                    ["stop", "prevent"]
                  )}
                  onMousedown={withModifiers(Void, ["stop", "prevent"])}
                  onMouseup={withModifiers(Void, ["stop", "prevent"])}
                >
                  <a class="d-flex justify-content-between align-items-center">
                    <span innerHTML={info.unicode} />
                    <small
                      class={{
                        "onhover-show-visibility": info.id != this.active_key,
                      }}
                    >
                      {info.group}
                    </small>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )
  },
  watch: {
    selection() {
      // @ts-expect-error TS2345 TODO: fix vue tsx types
      NextFrame(() => fire_event(this.$refs.hidden, "change"))
    },
  },
})
